import { useEffect, useState } from 'react';
import VideoLightbox from './VideoLightbox';
import MediaKitInterestTags from './MediaKitInterestTags';
import MediaKitSocialOverview from './MediaKitSocialOverview';
import MediaKitHighlightCards from './MediaKitHighlightCards';
import MediaKitHighlightsEdit from './MediaKitHighlightsEdit';
import MediaKitPhotoCollage from './MediaKitPhotoCollage';
import MediaKitPhotoCollageEdit from './MediaKitPhotoCollageEdit';
import MediaKitIntroReel from './MediaKitIntroReel';
import MediaKitEditButton from './MediaKitEditButton';
import MediaKitSectionHeader from './MediaKitSectionHeader';
import MediaKitInterestsEdit from './MediaKitInterestsEdit';
import MediaKitSocialEdit from './MediaKitSocialEdit';
import {
    MediaKitData,
    updateMediaKit,
    isValidIntroVideoUrl,
} from '../services/mediaKitService';
import {
    MEDIA_KIT_MAX_ABOUT_CHARS,
    validateMediaKitAbout,
    truncateMediaKitField,
} from '../utils/mediaKitValidation';
import '../styles/media-kit.css';

interface MediaKitViewProps {
    data: MediaKitData;
    athleteUserId: number;
    onUpdated: (data: MediaKitData) => void;
    readOnly?: boolean;
}

type EditSection = 'about' | 'socials' | 'interests' | 'video' | 'highlights' | null;

export default function MediaKitView({ data, athleteUserId, onUpdated, readOnly = false }: MediaKitViewProps) {
    const [editSection, setEditSection] = useState<EditSection>(null);
    const [aboutDraft, setAboutDraft] = useState(data.aboutYou ?? '');
    const [introDraft, setIntroDraft] = useState(data.introVideoUrl ?? '');
    const [videoError, setVideoError] = useState<string | null>(null);
    const [aboutError, setAboutError] = useState<string | null>(null);
    const [showVideo, setShowVideo] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (editSection !== 'about') {
            setAboutDraft(data.aboutYou ?? '');
        }
        if (editSection !== 'video') {
            setIntroDraft(data.introVideoUrl ?? '');
        }
    }, [data, editSection]);

    const sportYear = [data.sport, data.graduationYear].filter(Boolean).join(' | ');

    const saveAbout = async () => {
        const validationError = validateMediaKitAbout(aboutDraft);
        if (validationError) {
            setAboutError(validationError);
            return;
        }
        setAboutError(null);
        setSaving(true);
        try {
            const updated = await updateMediaKit({ aboutYou: aboutDraft }, athleteUserId);
            onUpdated(updated);
            setEditSection(null);
        } finally {
            setSaving(false);
        }
    };

    const saveIntroVideo = async () => {
        if (introDraft.trim() && !isValidIntroVideoUrl(introDraft)) {
            setVideoError('Please enter a valid YouTube or Vimeo URL.');
            return;
        }
        setVideoError(null);
        setSaving(true);
        try {
            const updated = await updateMediaKit({ introVideoUrl: introDraft }, athleteUserId);
            onUpdated(updated);
            setEditSection(null);
        } finally {
            setSaving(false);
        }
    };

    const openSocialEdit = () => {
        setEditSection('socials');
    };

    const openInterestsEdit = () => {
        setEditSection('interests');
    };

    const openHighlightsEdit = () => {
        setEditSection('highlights');
    };

    const allInterests = [
        ...data.selectedInterests,
        ...(data.customInterests ? data.customInterests.split(',').map((s) => s.trim()).filter(Boolean) : []),
    ];
    const canEdit = !readOnly;
    const activeEditSection = canEdit ? editSection : null;
    const hasPhotos = data.photos.length > 0;
    const showPhotoCollage = hasPhotos || canEdit;

    return (
        <div className="media-kit-view" data-testid="media-kit-view">
            <div
                className={`media-kit-section media-kit-profile-header${activeEditSection === 'about' ? ' media-kit-profile-header--editing' : ''}${!showPhotoCollage ? ' media-kit-profile-header--copy-only' : ''}`}
            >
                {canEdit && (
                    <MediaKitEditButton
                        label="Edit about and photos"
                        onClick={() => {
                            setAboutDraft(data.aboutYou ?? '');
                            setEditSection('about');
                        }}
                    />
                )}
                <div className="media-kit-profile-copy">
                    <h1 className="media-kit-name">
                        {data.firstName && <span className="media-kit-name-line">{data.firstName}</span>}
                        {data.lastName && <span className="media-kit-name-line">{data.lastName}</span>}
                    </h1>
                    <p className="media-kit-sport-year">{sportYear}</p>
                    {activeEditSection === 'about' ? (
                        <div className="media-kit-profile-edit-form">
                            <textarea
                                className="media-kit-textarea"
                                value={aboutDraft}
                                onChange={(e) => {
                                    setAboutDraft(truncateMediaKitField(e.target.value, MEDIA_KIT_MAX_ABOUT_CHARS));
                                    setAboutError(null);
                                }}
                                maxLength={MEDIA_KIT_MAX_ABOUT_CHARS}
                            />
                            {aboutError && <span className="media-kit-error">{aboutError}</span>}
                            <div className="media-kit-profile-edit-actions">
                                <button type="button" className="media-kit-btn-primary" onClick={saveAbout} disabled={saving}>
                                    Save
                                </button>
                                <button type="button" className="media-kit-edit-cancel-btn" onClick={() => setEditSection(null)}>
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="media-kit-about">{data.aboutYou || '—'}</p>
                    )}
                </div>
                {showPhotoCollage && (activeEditSection === 'about' ? (
                    <MediaKitPhotoCollageEdit
                        photos={data.photos}
                        athleteUserId={athleteUserId}
                        onPhotosChange={(photos) => onUpdated({ ...data, photos })}
                        onUpdated={(kit) => onUpdated({ ...data, photos: kit.photos })}
                    />
                ) : (
                    <MediaKitPhotoCollage photos={data.photos} />
                ))}
            </div>

            <div className="media-kit-section media-kit-connect-section">
                <MediaKitSectionHeader title="Connect" editLabel="Edit socials" onEdit={openSocialEdit} editable={canEdit} />
                {activeEditSection === 'socials' ? (
                    <MediaKitSocialEdit
                        socials={data.socials}
                        athleteUserId={athleteUserId}
                        onSaved={(updated) => {
                            onUpdated(updated);
                            setEditSection(null);
                        }}
                        onCancel={() => setEditSection(null)}
                    />
                ) : (
                    <MediaKitSocialOverview socials={data.socials} />
                )}
            </div>

            <div className="media-kit-two-col">
                <div className="media-kit-section">
                    <MediaKitSectionHeader title="Interests" editLabel="Edit interests" onEdit={openInterestsEdit} editable={canEdit} />
                    {activeEditSection === 'interests' ? (
                        <MediaKitInterestsEdit
                            selectedInterests={data.selectedInterests}
                            customInterests={data.customInterests ?? ''}
                            athleteUserId={athleteUserId}
                            onSaved={(updated) => {
                                onUpdated(updated);
                                setEditSection(null);
                            }}
                            onCancel={() => setEditSection(null)}
                        />
                    ) : (
                        <MediaKitInterestTags interests={allInterests} />
                    )}
                </div>
                <div className="media-kit-section">
                    <MediaKitSectionHeader
                        title="Intro Reel"
                        editLabel="Edit intro video"
                        editable={canEdit}
                        onEdit={() => {
                            setIntroDraft(data.introVideoUrl ?? '');
                            setEditSection('video');
                        }}
                    />
                    {activeEditSection === 'video' ? (
                        <div className="media-kit-intro-edit-form">
                            <input
                                className="media-kit-input"
                                value={introDraft}
                                onChange={(e) => {
                                    setIntroDraft(e.target.value);
                                    setVideoError(null);
                                }}
                                placeholder="Paste YouTube or Vimeo URL"
                                data-testid="media-kit-intro-video-edit"
                            />
                            {videoError && <span className="media-kit-error">{videoError}</span>}
                            <div className="media-kit-intro-edit-actions">
                                <button
                                    type="button"
                                    className="media-kit-btn-primary"
                                    onClick={saveIntroVideo}
                                    disabled={saving}
                                >
                                    Save
                                </button>
                                <button
                                    type="button"
                                    className="media-kit-edit-cancel-btn"
                                    onClick={() => {
                                        setIntroDraft(data.introVideoUrl ?? '');
                                        setVideoError(null);
                                        setEditSection(null);
                                    }}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : data.introVideoUrl ? (
                        <MediaKitIntroReel introVideoUrl={data.introVideoUrl} onPlay={() => setShowVideo(true)} />
                    ) : (
                        <p className="media-kit-empty-copy">No intro video</p>
                    )}
                </div>
            </div>

            {(canEdit || data.highlights.length > 0) && (
                <div className="media-kit-highlights-section">
                    <MediaKitSectionHeader
                        className="media-kit-highlights-heading"
                        titleClassName="media-kit-highlights-title"
                        title="Past Highlights"
                        editLabel="Edit highlights"
                        editable={canEdit}
                        onEdit={openHighlightsEdit}
                    />
                    {activeEditSection === 'highlights' ? (
                        <MediaKitHighlightsEdit
                            highlights={data.highlights}
                            athleteUserId={athleteUserId}
                            onSaved={(updated) => {
                                onUpdated(updated);
                                setEditSection(null);
                            }}
                            onCancel={() => setEditSection(null)}
                        />
                    ) : data.highlights.length > 0 ? (
                        <MediaKitHighlightCards highlights={data.highlights} />
                    ) : (
                        <p className="media-kit-empty-copy">No past highlights yet</p>
                    )}
                </div>
            )}

            {showVideo && data.introVideoUrl && (
                <VideoLightbox
                    open={showVideo}
                    resource={{ id: 0, title: 'Intro Reel', vimeoUrl: data.introVideoUrl }}
                    onClose={() => setShowVideo(false)}
                />
            )}
        </div>
    );
}
